import { useState, useMemo, useEffect } from 'react';
import styles from './EditPhonePageData.module.scss';
import { useForm, Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { fetcher } from 'app/http';
import qs from 'qs';
import { useAppDispatch } from 'app/hooks';
import PrefixSelect, {
  ICountryPrefix,
} from 'components/PhoneInput/PrefixSelect/PrefixSelect';
import { isNullOrWhiteSpace } from 'common/helpers/string';
import { formProgression, formError } from 'common/helpers/Gtm';
import TagManager from 'react-gtm-module';
import UpdatedFailedPopup from 'components/UpdateFailedPopup/UpdateFailedPopup';

interface Props {
  PageData?: any;
  Model?: any;
  LayoutModel?: any;
  handleClick: Function;
  handleClickMobileVerification: Function;
  onCancel: () => void;
  setEditPhoneSubmitting?: any;
  setEditPhoneConfirmationData?: any;
  setEditMobileConfirmationResponseData?: any;
}

const EditPhonePageData = ({
  Model,
  PageData,
  LayoutModel,
  handleClick,
  handleClickMobileVerification,
  setEditPhoneSubmitting,
  setEditPhoneConfirmationData,
  setEditMobileConfirmationResponseData,
}: Props) => {
  const [content, setContentPage] = useState<any>({ Model, PageData, LayoutModel });

  const getDefaultPrefix = (prefixes: ICountryPrefix[]) =>
    (prefixes || []).find((prefix) => prefix.Selected);

  const defaultPrefixMobile = useMemo(
    () => getDefaultPrefix(content.Model?.MobilePhoneCountryPrefixDropDown),
    [content.Model?.MobilePhoneCountryPrefixDropDown],
  );
  const defaultPrefixHome = useMemo(
    () => getDefaultPrefix(content.Model?.HomePhoneCountryPrefixDropDown),
    [content.Model?.HomePhoneCountryPrefixDropDown],
  );

  const {
    control,
    setValue,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      FormModel: {
        MobilePhoneNumber: content.Model?.FormModel?.MobilePhoneNumber,
        HomePhoneNumber: content.Model?.FormModel?.HomePhoneNumber,
        MobilePhoneNumberHidden:
          content.Model?.FormModel?.MobilePhoneNumberHidden,
        HomePhoneNumberHidden: content.Model?.FormModel?.HomePhoneNumberHidden,
        TimezoneOffsetInMinutes:
          content.Model?.FormModel?.TimezoneOffsetInMinutes,
        MobilePhoneCountryPrefixCode: defaultPrefixMobile?.Value,
        HomePhoneCountryPrefixCode: defaultPrefixHome?.Value,
      },
      PhoneType: content.Model?.PhoneType,
    },
    mode: 'all',
  });
  const [enableEditHomePhone, setEnableEditHomePhone] = useState(false);
  const [enableEditMobilePhone, setEnableEditMobilePhone] = useState(false);
  const [phoneType, setPhoneType] = useState(content.Model?.PhoneType);
  const [isValidInput, setIsValidInput] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [
    showInvalidHomePhoneNumberErrorMessage,
    setShowInvalidHomePhoneNumberErrorMessage,
  ] = useState(content?.Model?.ShowInvalidHomePhoneNumberErrorMessage);
  const [
    showInvalidMobilePhoneNumberErrorMessage,
    setShowInvalidPhonMobileNumberErrorMessage,
  ] = useState(content?.Model?.ShowInvalidMobilePhoneNumberErrorMessage);
  const [showUpdateFailedPopup, setShowUpdateFailedPopup] = useState<boolean>(content?.Model?.UpdateFailed);

  const getPathnameFromUrl = (url: string) => {
    const link = new URL(url);
    return link.pathname + link.search;
  };

  function enableEditHomePhoneNumber(e: any) {
    var timezoneOffset = new Date().getTimezoneOffset() * -1;
    setEnableEditHomePhone(true);
    setEnableEditMobilePhone(false);
    setPhoneType('Home');
    setValue('FormModel.TimezoneOffsetInMinutes', timezoneOffset);
  }

  function enableEditMobilePhoneNumber(e: any) {
    setEnableEditMobilePhone(true);
    setEnableEditHomePhone(false);
    setPhoneType('Mobile');
  }

  function cancelEditPhone(e: any) {
    handleClick(false);
  }

  function validatePhoneNumber(phoneNumber: string) {
    const re = /^[0-9]+$/;
    return re.test(phoneNumber);
  }

  function showverifiedicon(e: string) {
    let mobilenumbermin = content?.Model?.MobilePhoneNumberMinLength;
    let mobilenumbermax = content?.Model?.MobilePhoneNumberMaxLength;
    let homenumbermin = content?.Model?.HomePhoneNumberMinLength;
    let homenumbermax = content?.Model?.HomePhoneNumberMaxLength;
    console.log('eee', isNullOrWhiteSpace(e));
    if (!isNullOrWhiteSpace(e)) {
      if (
        (e.length >= homenumbermin &&
          e.length <= homenumbermax &&
          phoneType == 'Home') ||
        (e.length >= mobilenumbermin &&
          e.length <= mobilenumbermax &&
          phoneType == 'Mobile')
      ) {
        if (validatePhoneNumber(e)) {
          setIsValidInput(true);
          setShowInvalidHomePhoneNumberErrorMessage(false);
          setShowInvalidPhonMobileNumberErrorMessage(false);
        } else {
          setIsValidInput(false);
        }
      } else {
        setIsValidInput(false);
      }
    }
  }

  useEffect(() => {
    setContentPage({ Model, PageData, LayoutModel });
  }, [Model, PageData, LayoutModel]);

  useEffect(() => {
    if (content?.Model) {
      setShowInvalidHomePhoneNumberErrorMessage(
        content?.Model?.ShowInvalidHomePhoneNumberErrorMessage,
      );
      setShowInvalidPhonMobileNumberErrorMessage(
        content?.Model?.ShowInvalidMobilePhoneNumberErrorMessage,
      );
      setValue(
        'FormModel.MobilePhoneNumber',
        content.Model?.FormModel?.MobilePhoneNumber,
      );
      setValue(
        'FormModel.HomePhoneNumber',
        content.Model?.FormModel?.HomePhoneNumber,
      );
    }
  }, [content?.Model]);

  const history = useHistory();

  const onFormSubmit = handleSubmit(async (data: any) => {
    try {
      setIsSubmitDisabled(true);
      data['PhoneType'] = phoneType;
      data.FormModel.HomePhoneCountryPrefixCode =
        data.FormModel.HomePhoneCountryPrefixCode || defaultPrefixHome?.Value;
      data.FormModel.MobilePhoneCountryPrefixCode =
        data.FormModel.MobilePhoneCountryPrefixCode ||
        defaultPrefixMobile?.Value;

      const response = await fetcher.post(
        content.Model?.SendConfirmationCodePageUri,
        qs.stringify(data),
      );
      if (response && response.data && response.data.RedirectUrl) {
        const responseConfirmData = await fetcher.get(
          getPathnameFromUrl(response.data.RedirectUrl),
        );
        if (responseConfirmData && responseConfirmData.data) {
          if (
            phoneType == 'Mobile' &&
            responseConfirmData.data?.PageData?.PageTypeName !==
            'ContactDetailsUpdateConfirmationPageData'
          ) {
            setEditMobileConfirmationResponseData({
              PageData: responseConfirmData?.data?.PageData,
              Model: responseConfirmData?.data?.Model,
            });
            setEditPhoneSubmitting(false);
            handleClickMobileVerification(true);
            handleClick(false);
          } else {
            setEditPhoneConfirmationData({
              PageData: responseConfirmData?.data?.PageData,
              Model: responseConfirmData?.data?.Model,
            });
            setEditPhoneSubmitting(true);
            handleClickMobileVerification(false);
            handleClick(false);
            detailsPhoneTracking();
          }
        }
      } else if (response && response.data.status == 'error') {
        const redirectPath = getPathnameFromUrl(response.data.data);
        history.push(redirectPath);
      } else if (response && response.data) {
        setContentPage({
          PageData: response?.data?.PageData,
          Model: response?.data?.Model,
          LayoutModel: response?.data?.LayoutModel
        });
        handleClick(true);
        setEditPhoneSubmitting(false);
        setShowUpdateFailedPopup(response?.data?.Model?.UpdateFailed);
      }
    } catch (err) {
      console.error(err);
    }
    finally {
      setIsSubmitDisabled(false);
    }
  });
  var tagManagerArgs: any;
  useEffect(() => {
    if (errors.FormModel?.MobilePhoneNumber) {
      formError(
        'Profile phone form',
        'MobilePhoneNumber',
        'error_Incorrect_Mobile_Phone_Number',
      );
    }

    if (errors.FormModel?.HomePhoneNumber) {
      formError(
        'Profile phone form',
        'HomePhoneNumber',
        'error_Incorrect_Home_Phone_Number',
      );
    }
  }, [errors]);
  const detailsPhoneTracking = () => {
    tagManagerArgs = {
      dataLayer: {
        event: 'edit_profile',
        edit_section: 'Phone',
      },
    };
    TagManager.dataLayer(tagManagerArgs);
  };
  return (
    <>
      <form onSubmit={onFormSubmit}>
        <div className={styles.section}>
          <div className="Modal-content is-active">
            <div className="Grid Grid--gutterA Grid--gutter-sm-Bz">
              <div className="Grid-cell  u-lg-size1of1 u-paddingBz">
                <h3 className="Heading Heading-h3--portal u-marginBz">
                  {content.PageData?.PageHeader}
                </h3>
              </div>
              <div className="Grid-cell  u-lg-size1of2 data-hj-suppress">
                <div
                  className={
                    'form-group Input Input-button--after' +
                    (showInvalidHomePhoneNumberErrorMessage
                      ? ' error-message'
                      : '') +
                    (enableEditHomePhone && isValidInput ? ' valid' : '')
                  }
                >
                  <label className="Label Form-label">
                    {content.PageData?.HomePhoneNumberLabel}
                  </label>
                  <div
                    className={
                      'form-class form-input-number ' + styles.editphonenumber
                    }
                    id="editphonemodel"
                  >
                    <Controller
                      control={control}
                      name={'FormModel.HomePhoneCountryPrefixCode'}
                      render={({ field: { onChange, name, value } }) => (
                        <PrefixSelect
                          name={name}
                          value={value || defaultPrefixHome?.Value}
                          prefixes={
                            content?.Model?.HomePhoneCountryPrefixDropDown
                          }
                          className="Select u-marginBz u-marginHz"
                          onChange={onChange}
                          disabled={!enableEditHomePhone}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name={'FormModel.HomePhoneNumber'}
                      render={({
                        field: { onChange, name, value },
                        fieldState: { isDirty },
                      }) => {
                        if (isDirty) {
                          showverifiedicon(value);
                        }
                        return (
                          <input
                            type="number"
                            name={name}
                            className={
                              'form -class input-number  ' + styles.editPhoneClass
                            }
                            id="homeNumber"
                            disabled={!enableEditHomePhone}
                            value={value}
                            onChange={onChange}
                            onBlur={(e) =>
                              formProgression(
                                e,
                                'Profile Phone form',
                                'EditHomePhone',
                              )
                            }
                          />
                        );
                      }}
                    />
                    <input
                      type="hidden"
                      {...register('FormModel.HomePhoneNumberHidden')}
                      value={content.Model?.FormModel?.HomePhoneNumberHidden}
                    />
                    <input
                      type="hidden"
                      {...register('PhoneType')}
                      value={content.Model?.PhoneType}
                    />
                    <button
                      id="btnUnlockHomePhone"
                      type="button"
                      onClick={(e) => enableEditHomePhoneNumber(true)}
                      className={
                        'Button Input-button' +
                        (enableEditHomePhone || enableEditMobilePhone
                          ? ' u-hidden'
                          : '')
                      }
                    >
                      {content.PageData?.ChangeButtonLabel}
                    </button>
                  </div>
                </div>
                {showInvalidHomePhoneNumberErrorMessage && (
                  <p className="Text--small text-danger">
                    {content.PageData?.InvalidHomePhoneNumberErrorMessage}
                  </p>
                )}
              </div>

              <div className="Grid-cell  u-lg-size1of2 data-hj-suppress">
                <div
                  className={
                    'form-group Input Input-button--after' +
                    (showInvalidMobilePhoneNumberErrorMessage
                      ? ' error-message'
                      : '') +
                    (enableEditMobilePhone && isValidInput ? ' valid' : '')
                  }
                >
                  <label className="Label Form-label">
                    {content.PageData?.MobilePhoneNumberLabel}
                  </label>

                  <div
                    className={
                      'form-class form-input-number ' + styles.editphonenumber
                    }
                    id="editphonemodel"
                  >
                    <Controller
                      control={control}
                      name={'FormModel.MobilePhoneCountryPrefixCode'}
                      render={({ field: { onChange, name, value } }) => (
                        <PrefixSelect
                          name={name}
                          value={value || defaultPrefixMobile?.Value}
                          prefixes={
                            content?.Model?.MobilePhoneCountryPrefixDropDown
                          }
                          className="Select u-marginBz u-marginHz"
                          onChange={onChange}
                          disabled={!enableEditMobilePhone}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name={'FormModel.MobilePhoneNumber'}
                      render={({
                        field: { onChange, name, value },
                        fieldState: { isDirty },
                      }) => {
                        if (isDirty) {
                          showverifiedicon(value);
                        }
                        return (
                          <input
                            type="text"
                            name={name}
                            className={
                              'form -class input-number ' + styles.editPhoneClass
                            }
                            id="mobilePhone"
                            disabled={!enableEditMobilePhone}
                            aria-invalid={enableEditMobilePhone}
                            value={value}
                            onChange={onChange}
                            onBlur={(e) =>
                              formProgression(
                                e,
                                'Profile Phone form',
                                'EditMobilePhone',
                              )
                            }
                          />
                        );
                      }}
                    />
                    <input
                      type="hidden"
                      {...register('FormModel.MobilePhoneNumberHidden')}
                      value={content.Model?.FormModel?.MobilePhoneNumberHidden}
                    />
                    <button
                      id="btnUnlockMobilePhone"
                      type="button"
                      className={
                        'Button Input-button' +
                        (enableEditHomePhone || enableEditMobilePhone
                          ? ' u-hidden'
                          : '')
                      }
                      onClick={(e) => enableEditMobilePhoneNumber(true)}
                    >
                      {content.PageData?.ChangeButtonLabel}
                    </button>
                  </div>

                  <div></div>
                </div>
                {showInvalidMobilePhoneNumberErrorMessage && (
                  <p className="Text--small text-danger">
                    {content.PageData?.InvalidMobilePhoneNumberErrorMessage}
                  </p>
                )}
              </div>
              <div className="Grid-cell u-lg-size1of1 u-paddingVz">
                <div
                  className="Modal-infoText"
                  dangerouslySetInnerHTML={{
                    __html: PageData?.MobileBottomInformationLabel,
                  }}
                />
              </div>
              <div className="Grid-cell u-lg-size1of1">
                <div className="Customerdetails-modalButtons">
                  <div className="ButtonBar  ButtonBar--spaceBetween ButtonBar--row-reverse ">
                    <button
                      id="btnSubmit"
                      type="submit"
                      className={
                        enableEditHomePhone || enableEditMobilePhone
                          ? 'Button'
                          : 'Button is-disabled'
                      }
                      disabled={isSubmitDisabled}
                    >
                      <span>
                        {enableEditMobilePhone
                          ? content.PageData?.SendVerificationCodeButtonLabel
                          : content.PageData?.SubmitChangesButtonLabel}
                      </span>
                    </button>

                    <button
                      type="button"
                      id="BTN_cancel_edit_phone"
                      onClick={(e) => cancelEditPhone(e)}
                      className="Button Button--gray js-modalclose"
                    >
                      <span>{content.PageData?.CancelButtonLabel}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
      {showUpdateFailedPopup && (
        <>
          <UpdatedFailedPopup
            onClose={() => setShowUpdateFailedPopup(false)}
            {...content}
          />
        </>
      )}
    </>
  )
};

export default EditPhonePageData;
